<template>
  <AppLayout>
    <div class="col-12 layout-spacing">
      <div class="widget widget-six">
        <div class="widget-heading">
          <div class="row align-items-center">
            <div class="col-12">
              <h4 class="my-0">Watcher - odczyt pracy</h4>
              <button class="btn btn-success" @click="openAddModal">
                <i class="bi bi-plus-lg"></i> Dodaj
              </button>
            </div>
          </div>
        </div>

        <div class="widget-content">
          <div class="row align-items-center mb-3">
            <div class="col-auto mb-2">
              <select v-model.number="selectedDepartament" class="form-select form-sm" style="width: 300px"
                @change="loadAllDataForUsers">
                <option :value="0">WSZYSTKIE DZIAŁY</option>

                <option v-for="department in departaments" :key="department.id" :value="department.id">
                  {{ department.name }}
                </option>

                <option :value="-1">POZOSTALI PRACOWNICY</option>
              </select>
            </div>

            <div class="col-auto mb-2">
              <div class="btn-group" role="group">
                <button type="button" class="btn btn-sm"
                  :class="viewMode === 'day' ? 'btn-primary' : 'btn-outline-primary'" @click="changeViewMode('day')">
                  Dzień
                </button>

                <button type="button" class="btn btn-sm"
                  :class="viewMode === 'month' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="changeViewMode('month')">
                  Miesiąc
                </button>
              </div>
            </div>

            <div v-if="viewMode === 'day'" class="col-auto mb-2">
              <input v-model="selectedDate" type="date" class="form-control form-sm" @change="loadAllDataForUsers" />
            </div>

            <div v-if="viewMode === 'month'" class="col-auto mb-2">
              <input v-model="selectedMonth" type="month" class="form-control form-sm" @change="loadAllDataForUsers" />
            </div>

            <div class="col-auto mb-2">
              <button type="button" class="btn btn-primary btn-sm" :disabled="loading" @click="loadAllDataForUsers">
                {{ loading ? 'Pobieranie...' : 'Odśwież' }}
              </button>
            </div>
          </div>
          <div v-if="error" class="alert alert-danger">
            {{ error }}
          </div>
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary mb-2"></div>
            <div>Pobieranie danych...</div>
          </div>

          <div v-else>
            <div class="row mb-3">
              <div class="col-md-3 col-6 mb-2">
                <div class="summary-card">
                  <div class="summary-label">Pracownicy</div>
                  <div class="summary-value">
                    {{ users.length }}
                  </div>
                </div>
              </div>

              <div class="col-md-3 col-6 mb-2">
                <div class="summary-card">
                  <div class="summary-label">Komputery</div>
                  <div class="summary-value">
                    {{ computersCount }}
                  </div>
                </div>
              </div>

              <div class="col-md-3 col-6 mb-2">
                <div class="summary-card">
                  <div class="summary-label">Łączny czas pracy</div>
                  <div class="summary-value">
                    {{ formatDuration(totalDuration) }}
                  </div>
                </div>
              </div>

              <div class="col-md-3 col-6 mb-2">
                <div class="summary-card">
                  <div class="summary-label">Łączny AFK</div>
                  <div class="summary-value">
                    {{ formatDuration(totalAfk) }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="computersCount > 0">
              <div v-for="user in users" :key="user.id">
                <div v-for="computer in user.computers || []" :key="computerKey(user, computer)" class="computer">
                  <div class="computer-header" :class="{ 'has-projects': projectsOf(computer).length > 0 }"
                    @click="toggleComputer(computerKey(user, computer))">
                    <div class="computer-title col-md-6 col-6">
                      <span class="computer-arrow">
                        {{
                          isComputerExpanded(computerKey(user, computer))
                            ? '▼'
                            : '▶'
                        }}
                      </span>

                      <i class="fas fa-desktop"></i>

                      <div class="computer-user-info">
                        <div class="computer-name">
                          {{ user.name || 'Nieznany pracownik' }}
                        </div>

                        <div class="text-muted small">
                          {{ computer.hostname }}
                        </div>
                      </div>

                    </div>

                    <div class="plugin-info col-md-2 col-3">
                      <div v-if="computer.hostname" class="d-flex justify-content-center gap-2">
                        <a :href="`http://glpi.askla.lan:5600/#/activity/${encodeURIComponent(computer.hostname)}/view/`"
                          target="_blank" rel="noopener noreferrer" class="btn btn-outline-info btn-sm" @click.stop>
                          <i class="fas fa-external-link-alt me-1"></i>
                          Szczegóły
                        </a>

                        <a :href="`http://glpi.askla.lan:5600/#/buckets/aw-watcher-afk_${encodeURIComponent(computer.hostname)}-synced-from-${encodeURIComponent(computer.hostname)}`"
                          target="_blank" rel="noopener noreferrer" class="btn btn-outline-warning btn-sm" @click.stop>
                          <i class="fas fa-external-link-alt me-1"></i>
                          AFK
                        </a>
                      </div>
                    </div>
                    <div class="plugin-info col-md-2 col-12">
                      <div class="d-flex justify-content-center gap-2">
                        <button type="button" class="btn btn-outline-success btn-sm" title="Edytuj komputer"
                          @click.stop="openEditModal(user, computer)">
                          <i class="bi bi-pencil-square"></i>
                          Edytuj
                        </button>

                        <button type="button" class="btn btn-outline-danger btn-sm" title="Usuń komputer"
                          @click.stop="deleteWatcher(computer.id)">
                          <i class="bi bi-trash"></i>
                          Usuń
                        </button>
                      </div>
                    </div>

                    <div class="plugin-info col-md-1 col-3">
                      <div>
                        Czas pracy:
                        <strong class="plugin-time">
                          {{ formatDuration(computerDuration(computer)) }}
                        </strong>
                      </div>

                      <div>
                        AFK:
                        <strong class="afk-time">
                          {{ formatDuration(computerAfk(computer)) }}
                        </strong>
                      </div>

                      <div>
                        Projekty: {{ projectsOf(computer).length }}
                      </div>
                    </div>
                  </div>

                  <div v-if="isComputerExpanded(computerKey(user, computer))" class="computer-projects">
                    <div class="projects">
                      <div v-for="(project, projectIndex) in projectsOf(computer)"
                        :key="projectKey(user, computer, projectIndex)" class="project">
                        <div class="project-header" @click="toggleProject(projectKey(user, computer, projectIndex))">
                          <div class="project-name">
                            <span class="me-2">
                              {{
                                isProjectExpanded(projectKey(user, computer, projectIndex))
                                  ? '▼'
                                  : '▶'
                              }}
                            </span>

                            <i class="fas fa-folder me-2"></i>

                            {{ projectName(project) }}
                          </div>

                          <div class="project-direct">
                            Inventor:
                            {{ formatDuration(projectDirectDuration(project)) }}
                          </div>

                          <div class="project-total">
                            {{ formatDuration(projectDuration(project)) }}
                          </div>
                        </div>

                        <div v-if="isProjectExpanded(projectKey(user, computer, projectIndex))" class="project-details">
                          <!-- WIDOK DZIENNY: PLIKI -->
                          <template v-if="viewMode === 'day'">
                            <div v-for="(file, fileIndex) in filesOf(project)" :key="fileIndex" class="file">
                              <div>
                                <div class="file-name">
                                  <i class="far fa-file me-2"></i>
                                  {{ fileName(file) }}
                                </div>

                                <div class="file-path">
                                  {{ filePath(file) }}
                                </div>
                              </div>

                              <div class="file-time">
                                {{ formatDuration(fileDuration(file)) }}
                              </div>
                            </div>

                            <div v-if="filesOf(project).length === 0" class="empty">
                              Brak szczegółowych danych o plikach.
                            </div>

                            <div v-if="projectRemaining(project) > 0" class="remaining">
                              <span>Pozostałe</span>

                              <strong>
                                {{ formatDuration(projectRemaining(project)) }}
                              </strong>
                            </div>
                          </template>

                          <template v-else>
                            <div v-for="day in projectDays(project)" :key="day.date" class="day-row">
                              <div class="day-date">
                                {{ formatDate(day.date) }}
                              </div>

                              <div class="day-project">
                                {{ projectName(project) }}
                              </div>

                              <div class="day-time">
                                {{ formatDuration(day.duration) }}
                              </div>
                            </div>

                            <div v-if="projectDays(project).length === 0" class="empty">
                              Brak danych dziennych dla projektu.
                            </div>
                          </template>
                        </div>
                      </div>

                      <div v-if="projectsOf(computer).length === 0" class="empty">
                        Brak projektów w wybranym okresie.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="empty text-center">
              Brak danych dla wybranego okresu i działu.
            </div>
          </div>
        </div>

        <!-- Modal -->
        <div v-if="isModalOpen" class="modal fade show" tabindex="-1"
          style="display: block; background-color: rgba(0,0,0,0.5)">
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edytuj' : 'Dodaj' }}</h5>
                <button type="button" class="btn-close" @click="closeModal"></button>
              </div>
              <div class="modal-body">
                <div class="row">
                  <div class="col-md-12">
                    <label class="form-label">Pracownik</label>
                    <select v-model="form.user_id" class="form-select w-100" id="sel_user_id">
                      <option :value="null">Wybierz pracownika</option>

                      <option v-for="user in filteredUsers" :key="user.id" :value="user.id">
                        {{ user.last_name }} {{ user.first_name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-12 mt-3">
                    <label class="form-label">Hostname</label>
                    <input v-model="form.hostname" class="form-control" />
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-secondary" @click="closeModal">Anuluj</button>
                <button type="button" class="btn btn-success" :disabled="saving" @click="saveWatcher">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                  {{ saving ? 'Zapisywanie...' : (isEditing ? 'Zapisz zmiany' : 'Dodaj') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Koniec modal -->

      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from "@/Layouts/App.vue";
import axios from "axios";

export default {
  components: {
    AppLayout,
  },

  props: {
    departaments: {
      type: Array,
      default: () => [],
    },
    allusers: {
      type: Array,
      default: () => [],
    },
    departamentId: {
      type: Number,
      default: 0,
    },
  },

  data() {
    const today = new Date();
    const pad = value => String(value).padStart(2, "0");
    const todayString =
      today.getFullYear() + "-" + pad(today.getMonth() + 1) + "-" + pad(today.getDate());
    return {
      saving: false,
      users: [],
      loading: false,
      error: null,
      selectedDepartament: this.departamentId ?? 0,
      viewMode: "day",
      selectedDate: todayString,
      isModalOpen: false,
      isEditing: false,
      form: {
        id: null,
        user_id: null,
        hostname: "",
      },
      selectedMonth:
        today.getFullYear() + "-" + pad(today.getMonth() + 1),
      expandedComputers: [],
      expandedProjects: [],
    };
  },

  computed: {
    computersCount() {
      return this.users.reduce((total, user) => {
        return total + (user.computers || []).length;
      }, 0);
    },

    totalDuration() {
      return this.users.reduce((total, user) => {
        return total + (user.computers || []).reduce((sum, computer) => {
          return sum + this.computerDuration(computer);
        }, 0);
      }, 0);
    },

    totalAfk() {
      return this.users.reduce((total, user) => {
        return total + (user.computers || []).reduce((sum, computer) => {
          return sum + this.computerAfk(computer);
        }, 0);
      }, 0);
    },
    filteredUsers() {
      const departmentId = Number(this.selectedDepartament);

      // Wszystkie działy
      if (departmentId === 0) {
        return this.allusers;
      }

      // Pozostali pracownicy – bez działu
      if (departmentId === -1) {
        return this.allusers.filter(user => user.department_id === null);
      }

      // Wybrany konkretny dział
      return this.allusers.filter(user =>
        Number(user.department_id) === departmentId
      );
    },
  },

  mounted() {
    this.loadAllDataForUsers();
  },

  methods: {

    async changeViewMode(mode) {
      if (this.viewMode === mode) {
        return;
      }

      this.viewMode = mode;
      await this.loadAllDataForUsers();
    },

    async loadAllDataForUsers() {
      this.loading = true;
      this.error = null;

      this.expandedComputers = [];
      this.expandedProjects = [];

      try {
        const params = {
          departament: Number(this.selectedDepartament),
        };

        if (this.viewMode === "day") {
          const parts = this.selectedDate.split("-");

          params.year = Number(parts[0]);
          params.month = Number(parts[1]);
          params.day = Number(parts[2]);
        } else {
          const parts = this.selectedMonth.split("-");

          params.year = Number(parts[0]);
          params.month = Number(parts[1]);
        }

        const response = await axios.get("/watcher/report-data", {
          params,
        });

        if (!response.data || response.data.success === false) {
          throw new Error(
            response.data?.message || "Nie udało się pobrać raportu."
          );
        }

        this.users = response.data.employees || [];
      } catch (error) {
        console.error("Watcher report error:", error);

        this.users = [];

        this.error =
          error.response?.data?.message ||
          error.message ||
          "Nie udało się pobrać danych raportu.";
      } finally {
        this.loading = false;
      }
    },

    computerKey(user, computer) {
      return user.id + "-" + computer.hostname;
    },

    projectKey(user, computer, index) {
      return user.id + "-" + computer.hostname + "-project-" + index;
    },

    toggleComputer(key) {
      this.toggleArray(this.expandedComputers, key);
    },

    isComputerExpanded(key) {
      return this.expandedComputers.includes(key);
    },

    toggleProject(key) {
      this.toggleArray(this.expandedProjects, key);
    },

    isProjectExpanded(key) {
      return this.expandedProjects.includes(key);
    },

    toggleArray(array, value) {
      const index = array.indexOf(value);
      if (index === -1) {
        array.push(value);
      } else {
        array.splice(index, 1);
      }
    },

    normalizeArray(value) {
      if (Array.isArray(value)) {
        return value;
      }
      if (value && typeof value === "object") {
        return Object.values(value);
      }
      return [];
    },

    reportEntries(computer) {
      const report = computer.report;
      if (!report) {
        return [];
      }
      if (report.data && Array.isArray(report.data)) {
        return report.data;
      }
      if (Array.isArray(report)) {
        return report;
      }
      if (report.report) {
        return this.normalizeArray(report.report);
      }
      return [];
    },

    projectsOf(computer) {
      return this.reportEntries(computer).flatMap(entry => {
        return this.normalizeArray(entry.projects || []);
      });
    },

    projectName(project) {
      return (
        project.project ||
        project.name ||
        project.project_name ||
        "Bez nazwy projektu"
      );
    },

    filesOf(project) {
      return this.normalizeArray(project.files || []);
    },

    fileName(file) {
      if (typeof file === "string") {
        return file;
      }

      return file.document || "Nieznany plik";
    },

    filePath(file) {
      if (typeof file === "string") {
        return "";
      }

      return file.path || "";
    },

    computerDuration(computer) {
      return this.reportEntries(computer).reduce((sum, entry) => {
        const duration =
          entry.plugin?.duration ??
          entry.plugin_duration ??
          entry.duration ??
          0;

        return sum + Number(duration || 0);
      }, 0);
    },

    computerAfk(computer) {
      return this.reportEntries(computer).reduce((sum, entry) => {
        const afk = entry.afk;

        let duration = 0;

        if (typeof afk === "number") {
          duration = afk;
        } else if (afk && typeof afk === "object") {
          duration = afk.duration ?? afk.afk_duration ?? 0;
        } else {
          duration = entry.afk_duration ?? 0;
        }

        return sum + Number(duration || 0);
      }, 0);
    },
    projectDuration(project) {
      return Number(project.duration || 0);
    },

    projectDirectDuration(project) {
      return Number(project.direct_duration || 0);
    },

    projectRemaining(project) {
      return Math.max(
        0,
        this.projectDuration(project) - this.projectDirectDuration(project)
      );
    },

    fileDuration(file) {
      if (!file || typeof file !== "object") {
        return 0;
      }

      return Number(file.duration || 0);
    },

    projectDays(project) {
      const days = project.days;

      if (!days) {
        return [];
      }

      if (Array.isArray(days)) {
        return days.map(day => ({
          date: day.date || day.day || "",
          duration: Number(
            day.duration ??
            day.plugin_duration ??
            day.direct_duration ??
            0
          ),
        }));
      }
      return Object.entries(days)
        .map(([date, duration]) => ({
          date,
          duration: Number(
            typeof duration === "object"
              ? duration.duration ?? duration.plugin_duration ?? 0
              : duration
          ),
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
    },

    formatDate(date) {
      if (!date) {
        return "";
      }
      const parts = String(date).split("-");
      if (parts.length !== 3) {
        return date;
      }
      return parts[2] + "." + parts[1] + "." + parts[0];
    },

    formatDuration(seconds) {
      seconds = Math.floor(Number(seconds) || 0);
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return hours + " h " + minutes + " min";
    },


    openAddModal() {
      this.isEditing = false;

      this.form = {
        id: null,
        user_id: null,
        hostname: "",
      };

      this.isModalOpen = true;

      this.$nextTick(() => {
        this.initUserSelect();
      });
    },

    openEditModal(user, computer) {
      this.isEditing = true;

      this.form = {
        id: computer.id,
        user_id: Number(user.id),
        hostname: computer.hostname || "",
      };

      this.isModalOpen = true;

      this.$nextTick(() => {
        this.initUserSelect();
      });
    },

    initUserSelect() {
      const select = $("#sel_user_id");

      if (!select.length) return;

      if (select.hasClass("select2-hidden-accessible")) {
        select.select2("destroy");
      }

      select.select2({
        dropdownParent: $(".modal.show"),
        width: "100%",
        placeholder: "Wybierz pracownika",
      });

      select
        .val(this.form.user_id ? String(this.form.user_id) : "")
        .trigger("change");

      select.off("change.watcher").on("change.watcher", (e) => {
        const value = $(e.currentTarget).val();

        this.form.user_id = value ? Number(value) : null;
      });
    },

    closeModal() {
      const select = $("#sel_user_id");

      if (select.hasClass("select2-hidden-accessible")) {
        select.off("change.watcher");
        select.select2("destroy");
      }

      this.isModalOpen = false;

      this.form = {
        id: null,
        user_id: null,
        hostname: "",
      };
    },

    async saveWatcher() {
      if (!this.form.user_id) {
        this.showToast("warning", "Uwaga", "Wybierz pracownika.");
        return;
      }

      if (!this.form.hostname.trim()) {
        this.showToast("warning", "Uwaga", "Podaj hostname komputera.");
        return;
      }

      this.saving = true;

      try {
        const payload = {
          user_id: Number(this.form.user_id),
          hostname: this.form.hostname.trim(),
        };

        if (this.isEditing) {
          await axios.put(
            `/watcher/update/${this.form.id}`,
            payload
          );
        } else {
          await axios.post("/watcher/add", payload);
        }

        this.closeModal();

        await this.loadAllDataForUsers();

        this.showToast(
          "success",
          "Zapisano",
          this.isEditing
            ? "Dane komputera zostały zaktualizowane."
            : "Komputer został dodany."
        );

      } catch (error) {
        console.error("Watcher save error:", error);

        this.showToast(
          "error",
          "Błąd",
          error.response?.data?.message ||
          "Nie udało się zapisać danych."
        );

      } finally {
        this.saving = false;
      }
    },

    async deleteWatcher(id) {
      if (!id) {
        this.showToast("error", "Błąd", "Nie znaleziono ID komputera.");
        return;
      }

      if (!confirm("Czy na pewno chcesz usunąć ten komputer?")) {
        return;
      }

      try {
        await axios.delete(`/watcher/del/${id}`);

        await this.loadAllDataForUsers();

        this.showToast(
          "success",
          "Usunięto",
          "Komputer został usunięty."
        );

      } catch (error) {
        console.error("Watcher delete error:", error);

        this.showToast(
          "error",
          "Błąd",
          error.response?.data?.message ||
          "Nie udało się usunąć komputera."
        );
      }
    },

    showToast(icon, title, message) {
      Toast.fire({
        position: "top-end",
        toast: true,
        icon: icon,
        title: title,
        html: message,
        showClass: {
          popup: "animate__animated animate__fadeInDown"
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutUp"
        },
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true
      });
    },

  },
};
</script>

<style scoped>
.summary-card {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 14px;
  height: 100%;
}

.summary-label {
  font-size: 12px;
  color: #777;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 21px;
  font-weight: bold;
}

.computer {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  margin-bottom: 16px;
  overflow: hidden;
}

.computer-header {
  padding: 16px 18px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.computer-header.has-projects {
  cursor: pointer;
  user-select: none;
}

.computer-header.has-projects:hover {
  background: #f1f3f5;
}

.computer-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.computer-arrow {
  width: 18px;
  font-size: 14px;
  color: #777;
  text-align: center;
  flex-shrink: 0;
}

.computer-user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.computer-name {
  font-size: 17px;
  font-weight: bold;
}

.activitywatch-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 12px;
}

.activitywatch-container .btn {
  white-space: nowrap;
}

.plugin-info {
  flex-shrink: 0;
  min-width: 190px;
  text-align: right;
  font-size: 13px;
  color: #555;
  line-height: 1.7;
}

.plugin-time {
  color: #202124;
}

.afk-time {
  color: #777;
}

.projects {
  padding: 8px 18px 18px;
}

.project {
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  margin-top: 10px;
  overflow: hidden;
}

.project-header {
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 20px;
  align-items: center;
  cursor: pointer;
  background: #fff;
}

.project-header:hover {
  background: #f8f8f8;
}

.project-name {
  font-weight: bold;
}

.project-direct {
  font-size: 13px;
  color: #777;
  white-space: nowrap;
}

.project-total {
  font-weight: bold;
  white-space: nowrap;
}

.project-details {
  border-top: 1px solid #eee;
  padding: 8px 14px 14px;
  background: #fafafa;
}

.file {
  display: grid;
  grid-template-columns: 1fr 110px;
  gap: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.file:last-child {
  border-bottom: 0;
}

.file-name {
  font-weight: 600;
}

.file-path {
  color: #777;
  font-size: 12px;
  margin-top: 3px;
  word-break: break-all;
}

.file-time {
  text-align: right;
  white-space: nowrap;
}

.remaining {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #ccc;
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #666;
}

.day-row {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.day-row:last-child {
  border-bottom: 0;
}

.day-date {
  font-weight: 600;
}

.day-project {
  color: #777;
}

.day-time {
  font-weight: bold;
  white-space: nowrap;
}

.empty {
  padding: 18px;
  color: #777;
  font-size: 14px;
}

@media (max-width: 800px) {
  .computer-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .computer-title {
    width: 100%;
    flex-wrap: wrap;
  }

  .activitywatch-container {
    margin-left: 28px;
  }

  .plugin-info {
    text-align: left;
    min-width: 0;
  }

  .project-header {
    grid-template-columns: 1fr auto;
  }

  .project-direct {
    display: none;
  }

  .day-row {
    grid-template-columns: 1fr auto;
  }

  .day-project {
    display: none;
  }
}
</style>
